import React from 'react'
import PropTypes from 'prop-types'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { ICON, RADIUS, SURFACE, TEXT, tint } from '../../theme/tokens'

/**
 * The colours every icon action is drawn with, exported so the contrast test
 * checks exactly these: the icon at rest, its chip, and a disabled icon.
 */
export const ICON_ACTION = {
    rest: TEXT.secondary,
    chip: SURFACE.inputBg,
    disabled: TEXT.disabled,
}

/**
 * One icon action (Preview, Test, Download, Edit, Delete, More...) on a card
 * or a table row, the same everywhere.
 *
 * Always readable: the icon is drawn in the secondary text tier at 16px on a
 * faint chip with a hairline border, so it reads as a button before any
 * hover. The template cards had theirs at 13px and 40% opacity until the
 * card was hovered, the pipeline rows in the faintest text tier at 14px,
 * and both were reported as barely visible. Hover tints the icon and its
 * chip with `hoverColor`; disabled uses the disabled text tier (above 3:1),
 * never a lowered opacity.
 *
 * @param {{title: string, label?: string, onClick: function,
 *     hoverColor?: string, disabled?: boolean, popup?: string,
 *     stopPropagation?: boolean, children: node}} props `title` is the
 *     tooltip and, without `label`, the accessible name; `stopPropagation`
 *     keeps the click (and a disabled button's click-through) from reaching
 *     a clickable card around it; `popup` sets aria-haspopup (a menu)
 */
const IconAction = ({
    title,
    label,
    onClick,
    hoverColor = TEXT.primary,
    disabled = false,
    popup,
    stopPropagation = false,
    children,
}) => (
    <Tooltip title={title}>
        {/* The span keeps the tooltip on a disabled button and, on a card,
            stops a disabled button's click reaching the card. */}
        <span
            onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        >
            <IconButton
                size="small"
                aria-label={label ?? title}
                aria-haspopup={popup}
                disabled={disabled}
                onClick={(e) => {
                    if (stopPropagation) e.stopPropagation()
                    onClick(e)
                }}
                sx={{
                    p: '5px',
                    borderRadius: `${RADIUS.sm}px`,
                    color: ICON_ACTION.rest,
                    background: ICON_ACTION.chip,
                    border: `1px solid ${SURFACE.border}`,
                    '& svg': { fontSize: ICON.md },
                    '&:hover': {
                        color: hoverColor,
                        background: tint(hoverColor, 0.12),
                        borderColor: tint(hoverColor, 0.4),
                    },
                    '&.Mui-disabled': {
                        color: ICON_ACTION.disabled,
                        background: 'transparent',
                    },
                }}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
)

IconAction.propTypes = {
    title: PropTypes.string.isRequired,
    label: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    hoverColor: PropTypes.string,
    disabled: PropTypes.bool,
    popup: PropTypes.string,
    stopPropagation: PropTypes.bool,
    children: PropTypes.node.isRequired,
}

export default IconAction
